import batchStructureService from 'ApiService/Admin/batchService';
import batchTeacherStructureService from 'ApiService/Teacher/batch.teacher.service';
import learningMaterial from 'ApiService/Teacher/learningMaterial';
import testService from 'ApiService/Teacher/TestService';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const TrainingMaterialUpload = ({
  formData,
  setFormData,
  handleInputChange,
  setDisable,
  resetFlag,
}) => {
  const [selectedBatchStructure, setSelectedBatchStructure] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [previousMaterials, setPreviousMaterials] = useState([]);
  const [courses, setCourses] = useState([]);
  const { state } = useContext(AuthContext);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [errors, setErrors] = useState([]);
  const [batchStructures, setBatchStructures] = useState([]);
  const selectedBranch = state?.user?.branch?._id || state?.user?.branch?.name;
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  const [loadingBatchStructures, setLoadingBatchStructures] = useState(false);
  const [reuseCourseFilter, setReuseCourseFilter] = useState('');

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await testService.getAllCourses();
      if (response.data.success) {
        setCourses(response.data.courses);
      } else {
        toast.error('Failed to fetch courses.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const handleCourseChange = async (e) => {
    const courseId = e.target.value;
    if (!courseId) {
      setFormData({ ...formData, courseId: '' });
      setBatchStructures([]);
      setBatches([]);
      setSelectedBatchStructure('');
      setSelectedBatch('');
      return;
    }

    const course = courses.find((course) => course._id === courseId);
    if (!course) return;

    setFormData({ ...formData, courseId: course._id });
    setSelectedBatchStructure('');
    setSelectedBatch('');
    setBatches([]);

    setLoadingBatchStructures(true);
    try {
      const res = await batchTeacherStructureService.getBatchStructuresByCourse(
        courseId
      );
      if (res.data.success) {
        setBatchStructures(res.data.batchStructures || []);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingBatchStructures(false);
    }
  };
  const handleDayChange = (dayIndex, field, value) => {
    const updated = [...formData.days];
    updated[dayIndex][field] = value;

    setFormData({ ...formData, days: updated });
  };

  const handleMaterialChange = (dayIndex, subIndex, matIndex, field, value) => {
    const updated = [...formData.days];
    updated[dayIndex].subtopics[subIndex].materials[matIndex][field] = value;
    setFormData({ ...formData, days: updated });
  };

  const addMaterial = (dayIndex, subIndex) => {
    const updated = [...formData.days];
    updated[dayIndex].subtopics[subIndex].materials.push({
      type: 'content',
      content: '',
      file: null,
      fileUrl: '',
    });
    setFormData({ ...formData, days: updated });
  };

  const removeMaterial = (dayIndex, subIndex, matIndex) => {
    const updated = [...formData.days];
    updated[dayIndex].subtopics[subIndex].materials = updated[
      dayIndex
    ].subtopics[subIndex].materials.filter((_, i) => i !== matIndex);
    setFormData({ ...formData, days: updated });
  };

  const addDay = () => {
    const lastDay = formData.days[formData.days.length - 1];

    // 🚫 Prevent adding new day if previous incomplete
    if (!isDayValid(lastDay)) {
      toast.error('Please complete previous day first');
      return;
    }

    setFormData({
      ...formData,
      days: [
        ...formData.days,
        {
          lectureDay: formData.days.length + 1,
          topic: '',
          subtopics: [
            {
              title: '',
              materials: [
                {
                  type: 'content',
                  content: '',
                  file: null,
                  fileUrl: '',
                },
              ],
            },
          ],
        },
      ],
    });
  };

  const removeDay = (index) => {
    const updated = formData.days.filter((_, i) => i !== index);
    setFormData({ ...formData, days: updated });
  };

  const addSubtopic = (dayIndex) => {
    const day = formData.days[dayIndex];

    const lastSubtopic = day.subtopics[day.subtopics.length - 1];

    // 🚫 Prevent adding if last subtopic incomplete
    if (!isSubtopicValid(lastSubtopic)) {
      toast.error('Please complete previous subtopic first');
      return;
    }

    const updated = [...formData.days];

    updated[dayIndex].subtopics.push({
      title: '',
      materials: [
        {
          type: 'content',
          content: '',
          file: null,
          fileUrl: '',
        },
      ],
    });

    setFormData({ ...formData, days: updated });
  };
  const removeSubtopic = (dayIndex, subIndex) => {
    const updated = [...formData.days];

    updated[dayIndex].subtopics = updated[dayIndex].subtopics.filter(
      (_, i) => i !== subIndex
    );

    setFormData({ ...formData, days: updated });
  };

  const handleEnrollmentChange = async (index, field, value) => {
    // COURSE CHANGE → reset structure & batch
    if (field === 'course') {
      setBatchStructures([]);
      setBatches([]);

      if (value) {
        setLoadingBatchStructures(true);

        try {
          const res = await batchStructureService.getBatchStructuresByCourse(
            value
          );
          if (res.data.success) {
            setBatchStructures(res.data.batchStructures || []);
          }
        } catch {
          toast.error('Failed to load batch structures');
        } finally {
          setLoadingBatchStructures(false);
        }
      }
    }

    // BATCH STRUCTURE CHANGE → reset batch
    else if (field === 'batchStructure') {
      setFormData((prev) => ({
        ...prev,
        batchStructureId: value,
      }));
      setBatches([]);
      try {
        setLoadingBatches(true);
        const res =
          await batchTeacherStructureService.getBatchesByCourseStructure(
            formData.courseId,
            value,
            selectedBranch,
            true
          );

        if (res.data.success) {
          setBatches(res.data.batches || []);
          try {
            // setLoadingMaterials(true);

            const res = await learningMaterial.getMaterialsByStructure(
              value
            );

            if (res.data.success) {
              setPreviousMaterials(res.data.data || []);
            }
          } catch {
            toast.error('Failed to load previous materials');
          } finally {
            // setLoadingMaterials(false);
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Failed to load batches');
      } finally {
        setLoadingBatches(false);
      }
    } else if (field === 'batch') {
      setFormData({ ...formData, batchId: value });

      try {
        const res = await learningMaterial.getTrainingMaterial(
          value,
          formData.courseId
        );

        if (res.data.success && res.data.data) {
          const existingData = res.data.data;

          setFormData((prev) => ({
            ...prev,
            batchId: value,
            days: existingData.days, // 🔥 preload existing
          }));

          toast.success('Existing material loaded');
        } else {
          // ✅ No existing → keep default
          setFormData((prev) => ({
            ...prev,
            batchId: value,
            days: [
              {
                lectureDay: 1,
                topic: '',
                subtopics: [
                  {
                    title: '',
                    materials: [
                      {
                        type: 'content',
                        content: '',
                        file: null,
                        fileUrl: '',
                      },
                    ],
                  },
                ],
              },
            ],
          }));
        }
      } catch (err) {
        toast.error('Failed to load existing material');
      }
    }
  };

  useEffect(() => {
    const newErrors = [];

    const isInvalid =
      !formData.courseId ||
      !formData.batchId ||
      formData.days.length === 0 ||
      formData.days.some((day) => {
        if (!day.lectureDay || !day.topic) return true;

        if (!day.subtopics || day.subtopics.length === 0) return true;

        return day.subtopics.some((sub) => {
          if (!sub.title) return true;

          if (!sub.materials || sub.materials.length === 0) return true;

          return sub.materials.some((mat) => {
            if (mat.type === 'content') return !mat.content;
            return !mat.file && !mat.fileUrl;
          });
        });
      });

    if (isInvalid) {
      newErrors.push('All fields are mandatory.');
    }

    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [setDisable, formData]);

  useEffect(() => {
    setBatchStructures([]);
    setBatches([]);

    // if you added reuse feature
    setPreviousMaterials([]);
    setSelectedBatchStructure('');
    setSelectedBatch('');
  }, [resetFlag]);

  const isSubtopicValid = (sub) => {
    if (!sub.title) return false;

    if (!sub.materials || sub.materials.length === 0) return false;

    return sub.materials.every((mat) => {
      if (mat.type === 'content') {
        return !!mat.content;
      } else {
        return !!mat.file || !!mat.fileUrl;
      }
    });
  };

  const isDayValid = (day) => {
    return (
      day.topic &&
      day.subtopics.length > 0 &&
      day.subtopics.every(isSubtopicValid)
    );
  };

  const handleLoadPrevious = (material) => {
    if (!window.confirm('Replace current material?')) return;

    setFormData((prev) => ({
      ...prev,
      days: JSON.parse(JSON.stringify(material.days)), // ✅ IMPORTANT
    }));

    toast.success('Material loaded');
  };

  return (
    <div className="rounded-[20px] p-4">
      <h4 className="mb-6 text-xl font-bold text-navy-700 dark:text-white">
        Create Training Material
      </h4>

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-bold text-navy-700 dark:text-white">
              Course*
            </label>
            <select
              name="courseId"
              value={formData.courseId}
              onChange={handleCourseChange}
              className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
            >
              <option value="">Select Course</option>
              {loadingCourses ? (
                <option>Loading courses...</option>
              ) : courses.length === 0 ? (
                <option className="cursor-pointer font-bold text-brand-500">
                  No courses available
                </option>
              ) : (
                courses.map((course, i) => (
                  <option key={course._id} value={course._id}>
                    {i + 1}. {course.courseName}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* BATCH STRUCTURE */}
          <div>
            <label className="block text-sm font-medium">
              Batch Structure*
            </label>
            <select
              value={selectedBatchStructure}
              onChange={(e) => {
                setSelectedBatchStructure(e.target.value);
                handleEnrollmentChange(0, 'batchStructure', e.target.value);
              }}
              className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
            >
              <option value="">Select Structure</option>
              {loadingBatchStructures ? (
                <option>Loading...</option>
              ) : (
                batchStructures.map((bs) => (
                  <option key={bs._id} value={bs._id}>
                    {bs.name}
                  </option>
                ))
              )}
            </select>
          </div>
          {/* BATCH */}
          <div>
            <label className="block text-sm font-medium">Batch*</label>
            <select
              value={selectedBatch}
              onChange={(e) => {
                setSelectedBatch(e.target.value);
                handleEnrollmentChange(0, 'batch', e.target.value);
              }}
              className="mt-2 w-full md:min-w-[400px] rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
            >
              <option value="">Select Batch</option>

              {loadingBatches ? (
                <option>Loading...</option>
              ) : (
                batches.map((b) => {
                  const batchDisplay = `B${b.batchNumber}`;
                  
                  const date = new Date(b.startDate);
                  const day = date.getDate().toString().padStart(2, '0');
                  const month = date.toLocaleString('default', { month: 'short' });
                  const year = date.getFullYear().toString().slice(-2);
                  const dateDisplay = `${day}${month}${year}`;

                  const dayMap = {
                    Monday: 'M',
                    Tuesday: 'T',
                    Wednesday: 'W',
                    Thursday: 'Th',
                    Friday: 'F',
                    Saturday: 'S',
                    Sunday: 'Su',
                  };

                  const scheduleDisplay = b.daySchedules
                    ?.map((schedule) => {
                      const dayShort = dayMap[schedule.day] || schedule.day;
                      const time = schedule.startTime.replace(':00', '').replace(' ', '');
                      return `${dayShort}-${time}`;
                    })
                    .join(' | ');

                  const branchInitials = b.branches
                    ?.map((br) => br.name.split(',').pop().trim()[0])
                    .join('');

                  const schedulePart = scheduleDisplay ? `(${scheduleDisplay} )` : '';
                  const fullDisplay = `${batchDisplay} | ${dateDisplay} ${schedulePart} ${branchInitials || ''}`.trim();

                  return (
                    <option key={b._id} value={b._id}>
                      {fullDisplay}
                    </option>
                  );
                })
              )}
            </select>
          </div>
          {previousMaterials.length > 0 && (
            <div className="col-span-2 mt-4 rounded-xl bg-gray-50 p-4 dark:bg-navy-900/20">
              <div className="mb-4 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                <h3 className="text-sm font-bold">Reuse Previous Material</h3>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-500">
                    Filter by Course:
                  </label>
                  <select
                    value={reuseCourseFilter}
                    onChange={(e) => setReuseCourseFilter(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                  >
                    <option value="">All Courses</option>
                    {[
                      ...new Set(
                        previousMaterials
                          .map((m) => m.course?.courseName)
                          .filter(Boolean)
                      ),
                    ].map((courseName) => (
                      <option key={courseName} value={courseName}>
                        {courseName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {previousMaterials
                  .filter((mat) =>
                    reuseCourseFilter
                      ? mat.course?.courseName === reuseCourseFilter
                      : true
                  )
                  .map((mat) => (
                    <button
                      key={mat._id}
                      type="button"
                      onClick={() => handleLoadPrevious(mat)}
                      className="group relative rounded-lg border bg-white px-4 py-2 text-left transition-all hover:border-brand-500 hover:bg-brand-50 dark:border-white/10 dark:bg-navy-700 dark:hover:bg-navy-600"
                    >
                      <div className="text-xs font-bold text-brand-500">
                        {mat.course?.courseName || 'N/A'}
                      </div>
                      <div className="text-sm font-medium dark:text-white">
                        Batch {mat.batch?.batchNumber} (
                        {new Date(mat.batch?.startDate).toLocaleDateString(
                          'en-GB'
                        )}
                        )
                      </div>
                      <div className="text-[10px] text-gray-500">
                        By: {mat.teacher?.name || 'Admin'}
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          )}
          <br />
          {formData.days.map((day, dayIndex) => (
            <div
              key={dayIndex}
              className="col-span-2 mt-4 rounded-xl border p-4"
            >
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-lg font-bold">Day {dayIndex + 1}</h3>

                {formData.days.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDay(dayIndex)}
                    className="text-red-500"
                  >
                    Remove Day
                  </button>
                )}
              </div>

              <h3 className="text-sm font-bold">Enter Topic*</h3>
              {/* Topic */}
              <input
                type="text"
                value={day.topic}
                onChange={(e) =>
                  handleDayChange(dayIndex, 'topic', e.target.value)
                }
                placeholder="Enter Topic"
                className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
              />

              {/* Subtopics */}
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {day.subtopics.map((sub, subIndex) => (
                  <div
                    key={subIndex}
                    className="space-y-2 rounded-xl border p-4"
                  >
                    <h3 className="text-sm font-bold">Enter Subtopic*</h3>
                    <input
                      type="text"
                      value={sub.title}
                      onChange={(e) => {
                        const updated = [...formData.days];
                        updated[dayIndex].subtopics[subIndex].title =
                          e.target.value;
                        setFormData({ ...formData, days: updated });
                      }}
                      className="text-black mt-2 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm placeholder-gray-400 outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                    />

                    <h3 className="text-sm font-bold">Select Material Type*</h3>
                    {/* 🔥 MATERIALS LOOP */}
                    {sub.materials.map((mat, matIndex) => (
                      <div
                        key={matIndex}
                        className="mt-3 rounded-lg border p-3"
                      >
                        <select
                          value={mat.type}
                          onChange={(e) =>
                            handleMaterialChange(
                              dayIndex,
                              subIndex,
                              matIndex,
                              'type',
                              e.target.value
                            )
                          }
                          className="text-black mt-2 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm placeholder-gray-400 outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                        >
                          <option value="content">Text</option>
                          <option value="VIDEO">Video</option>
                          <option value="PDF">PDF</option>
                          <option value="IMAGE">Image</option>
                        </select>

                        {mat.type === 'content' ? (
                          <textarea
                            value={mat.content}
                            onChange={(e) =>
                              handleMaterialChange(
                                dayIndex,
                                subIndex,
                                matIndex,
                                'content',
                                e.target.value
                              )
                            }
                            className="text-black mt-2 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm placeholder-gray-400 outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
                          />
                        ) : (
                          <input
                            type="file"
                            accept={
                              mat.type === 'VIDEO'
                                ? 'video/*'
                                : mat.type === 'IMAGE'
                                ? 'image/*'
                                : '.pdf'
                            }
                            onChange={(e) => {
                              const file = e.target.files[0];

                              if (!file) return;

                              // ✅ ADD THIS
                              if (file.size > 1024 * 1024 * 1024) {
                                toast.error('File must be less than 1GB');
                                return;
                              }

                              handleMaterialChange(
                                dayIndex,
                                subIndex,
                                matIndex,
                                'file',
                                file
                              );
                            }}
                            className="mt-2"
                          />
                        )}

                        {/* Preview */}
                        {mat.fileUrl && mat.type === 'VIDEO' && (
                          <video width="200" controls className="mt-2">
                            <source
                              src={`${process.env.REACT_APP_API_BASE_URL}/${mat.fileUrl}`}
                            />
                          </video>
                        )}

                        {mat.fileUrl && mat.type === 'IMAGE' && (
                          <img
                            src={`${process.env.REACT_APP_API_BASE_URL}/${mat.fileUrl}`}
                            alt="Material Preview"
                            className="mt-2 h-32"
                          />
                        )}

                        {mat.fileUrl && mat.type === 'PDF' && (
                          <a
                            href={`${process.env.REACT_APP_API_BASE_URL}/${mat.fileUrl}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-500 underline"
                          >
                            View PDF
                          </a>
                        )}

                        {sub.materials.length > 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              removeMaterial(dayIndex, subIndex, matIndex)
                            }
                            className="mt-2 text-sm text-red-500"
                          >
                            Remove Material
                          </button>
                        )}
                      </div>
                    ))}
                    <div className="flex justify-between">
                      <button
                        type="button"
                        onClick={() => addMaterial(dayIndex, subIndex)}
                        className="mt-2 text-sm text-blue-600"
                      >
                        + Add Material
                      </button>

                      {day.subtopics.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSubtopic(dayIndex, subIndex)}
                          className="text-sm text-red-500"
                        >
                          Remove Subtopic
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => addSubtopic(dayIndex)}
                disabled={
                  !isSubtopicValid(day.subtopics[day.subtopics.length - 1])
                }
                className={`mt-3 rounded px-4 py-2 text-white ${
                  isSubtopicValid(day.subtopics[day.subtopics.length - 1])
                    ? 'bg-brand-500'
                    : 'cursor-not-allowed bg-gray-400'
                }`}
              >
                + Add Subtopic
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addDay}
          disabled={!isDayValid(formData.days[formData.days.length - 1])}
          className={`mt-5 rounded px-5 py-2 text-white ${
            isDayValid(formData.days[formData.days.length - 1])
              ? 'bg-green-600'
              : 'cursor-not-allowed bg-gray-400'
          }`}
        >
          + Add New Day
        </button>
      </div>
      {errors.length > 0 && (
        <div className="mt-2 text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrainingMaterialUpload;
